import { createSyncStore } from '../factory';

type NotificationPlacement = 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left';

type NotificationSettingsState = {
  placement: NotificationPlacement;
  mode: 'default' | 'new';
};

type NotificationSettingsActions = {
  setPlacement: (placement: NotificationPlacement) => void;
  setMode: (mode: 'default' | 'new') => void;
};

/**
 * Synced store for notification settings (placement + mode) shared between
 * the main window, notification (alert) window, and the shell.
 *
 * Replaces the old `notification:state`, `notification:placement`, and
 * `notification:initial-state` IPC patterns.
 *
 * The shell pushes initial state via
 * `shellBridge.setState('notification-settings', { placement, mode })`.
 *
 * The shell subscribes via
 * `shellBridge.subscribe('notification-settings', cb, ['placement', 'mode'])`
 * to persist changes and reposition the alert window.
 *
 * Renderers set placement via
 * `useShellNotificationSettingsStore.getState().setPlacement(...)`.
 *
 * Channel: `notification-settings`
 */
export const useShellNotificationSettingsStore = createSyncStore<
  NotificationSettingsState,
  NotificationSettingsActions
>({
  channel: 'notification-settings',
  syncKeys: ['placement', 'mode'],
  throttleMs: 50,
  syncOptions: {
    broadcastInitialState: false,
  },

  state: {
    placement: 'top-right',
    mode: 'default',
  },

  actions: (set) => ({
    setPlacement: (placement: NotificationPlacement) => set({ placement }),
    setMode: (mode: 'default' | 'new') => set({ mode }),
  }),
});
